import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { markCheckedIn } from '../services/squareApi';

const { FiCheck, FiArrowLeft, FiCalendar, FiClock, FiUser } = FiIcons;

function AppointmentLookup({ 
  customerData, 
  appointmentData, 
  onNavigate, 
  setIsLoading, 
  setError 
}) {
  const handleCheckIn = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await markCheckedIn(appointmentData.id);
      if (result.success) {
        onNavigate('photo_upload');
      } else {
        setError(result.error || 'Failed to check in. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="text-center"
      >
        <div className="text-6xl mb-4">🎯</div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          We found your appointment!
        </h2>
        <p className="text-slate-600">
          Here's what we found for you today:
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-slate-50 rounded-xl p-4 space-y-3"
      >
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiUser} className="w-5 h-5 text-[#0C1C35]" />
          <div>
            <p className="font-medium text-slate-800">
              {customerData?.given_name} {customerData?.family_name}
            </p>
            <p className="text-sm text-slate-600">
              {customerData?.email_address || customerData?.phone_number}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SafeIcon icon={FiCalendar} className="w-5 h-5 text-[#0C1C35]" />
          <p className="text-slate-700">
            {appointmentData?.start_at && formatDate(appointmentData.start_at)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <SafeIcon icon={FiClock} className="w-5 h-5 text-[#0C1C35]" />
          <p className="text-slate-700">
            {appointmentData?.start_at && formatTime(appointmentData.start_at)} - 
            {appointmentData?.end_at && formatTime(appointmentData.end_at)}
          </p>
        </div>

        {appointmentData?.appointment_segments?.[0]?.service_variation?.name && (
          <div className="pt-2 border-t border-slate-200">
            <p className="text-sm font-medium text-slate-800">Service:</p>
            <p className="text-sm text-slate-600">
              {appointmentData.appointment_segments[0].service_variation.name}
            </p>
          </div>
        )}
      </motion.div>

      <div className="space-y-3">
        <button
          onClick={handleCheckIn}
          className="w-full bg-[#0C1C35] text-white py-4 rounded-xl font-medium hover:bg-blue-900 transition-colors flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiCheck} className="w-5 h-5" />
          Yes, that's me — I've arrived!
        </button>

        <button
          onClick={() => onNavigate('welcome')}
          className="w-full border-2 border-slate-300 text-slate-700 py-4 rounded-xl font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          That's not me
        </button>
      </div>
    </div>
  );
}

export default AppointmentLookup;